// SEO + structured data helpers.
//
// The site is a SPA, so meta tags are injected client-side via react-helmet-async.
// Modern crawlers (Googlebot, Bingbot) execute JavaScript and pick these up.

const FALLBACK_ORIGIN = "https://raisesrilanka.org";

export function siteOrigin(): string {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return FALLBACK_ORIGIN;
}

export function absoluteUrl(path: string = "/"): string {
  if (path.startsWith("http")) return path;
  const origin = siteOrigin().replace(/\/$/, "");
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${origin}${p}`;
}

export function truncate(input: string | null | undefined, max = 155): string {
  if (!input) return "";
  const clean = input.replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  return clean.slice(0, max - 1).replace(/\s+\S*$/, "") + "…";
}

export function formatUSDPlain(cents: number | null | undefined): string {
  const n = Math.round((cents ?? 0) / 100);
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}

// ────────────────────────────────────────────────────────────────────────────
// JSON-LD builders
// ────────────────────────────────────────────────────────────────────────────

export function buildOrgJsonLd(opts?: { sameAs?: string[]; email?: string; phone?: string; logo?: string }) {
  const origin = siteOrigin();
  const sameAs = (opts?.sameAs ?? []).filter(Boolean);
  return {
    "@context": "https://schema.org",
    // Multi-type: NGO + NonprofitOrganization is the most specific signal for
    // Google's Knowledge Graph and for LLM citation engines like Perplexity.
    "@type": ["NGO", "NonprofitOrganization"],
    "@id": `${origin}#organization`,
    name: "Raise Sri Lanka",
    legalName: "Raise Sri Lanka",
    alternateName: "Raise Sri Lanka 501(c)(3)",
    url: origin,
    logo: opts?.logo ?? `${origin}/favicon.png`,
    image: opts?.logo ?? `${origin}/favicon.png`,
    description:
      "Raise Sri Lanka is a US-registered 501(c)(3) nonprofit funding education, health, social upliftment, and disaster recovery projects across Sri Lanka. 100% volunteer-run.",
    slogan: "Bring hope to every Sri Lankan family",
    foundingLocation: { "@type": "Place", name: "United States" },
    areaServed: [
      { "@type": "Country", name: "Sri Lanka" },
      { "@type": "Country", name: "United States" },
    ],
    knowsAbout: [
      "Sri Lanka humanitarian aid",
      "501(c)(3) charitable giving",
      "Education funding in Sri Lanka",
      "Medical aid in Sri Lanka",
      "Disaster recovery in Sri Lanka",
    ],
    nonprofitStatus: "Nonprofit501c3",
    // EIN can be added here as `taxID: "XX-XXXXXXX"` once published; omitted
    // intentionally until verified to avoid mis-attribution.
    sameAs,
    ...(opts?.email || opts?.phone
      ? {
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "customer support",
            ...(opts.email ? { email: opts.email } : {}),
            ...(opts.phone ? { telephone: opts.phone } : {}),
            availableLanguage: ["English", "Sinhala", "Tamil"],
          },
        }
      : {}),
  };
}

export function buildWebsiteJsonLd() {
  const origin = siteOrigin();
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Raise Sri Lanka",
    url: origin,
    inLanguage: "en",
    potentialAction: {
      "@type": "SearchAction",
      target: `${origin}/projects?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
}

export function buildBreadcrumbsJsonLd(crumbs: { name: string; url: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: absoluteUrl(c.url),
    })),
  };
}

export type ProjectSeo = {
  id: string;
  slug: string;
  title: string;
  short_description?: string | null;
  story?: string | null;
  hero_image?: string | null;
  location?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  status?: string | null;
  goal_cents?: number | null;
  raised_cents?: number | null;
  completed_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  categories?: { name?: string | null; slug?: string | null } | null;
};

export function buildProjectJsonLd(p: ProjectSeo) {
  const url = absoluteUrl(`/projects/${p.slug}`);
  const description = truncate(p.short_description || p.story, 300);
  const blocks: object[] = [];

  // Article schema for the project story
  blocks.push({
    "@context": "https://schema.org",
    "@type": "Article",
    headline: p.title,
    description,
    image: p.hero_image ? [p.hero_image] : undefined,
    datePublished: p.created_at ?? undefined,
    dateModified: p.updated_at ?? p.created_at ?? undefined,
    mainEntityOfPage: url,
    inLanguage: "en",
    author: { "@type": "Organization", name: "Raise Sri Lanka" },
    publisher: {
      "@type": "Organization",
      name: "Raise Sri Lanka",
      logo: { "@type": "ImageObject", url: `${siteOrigin()}/favicon.png` },
    },
    about: p.categories?.name
      ? { "@type": "Thing", name: p.categories.name }
      : undefined,
  });

  // GEO: place / local-business signal when we have a location
  if (p.location || (p.latitude && p.longitude)) {
    blocks.push({
      "@context": "https://schema.org",
      "@type": "Place",
      name: p.title,
      address: p.location
        ? {
            "@type": "PostalAddress",
            addressLocality: p.location,
            addressCountry: "LK",
          }
        : undefined,
      geo:
        p.latitude && p.longitude
          ? {
              "@type": "GeoCoordinates",
              latitude: p.latitude,
              longitude: p.longitude,
            }
          : undefined,
    });
  }

  // Donate action — only meaningful for active fundable projects
  if (p.status !== "completed" && (p.goal_cents ?? 0) > 0) {
    blocks.push({
      "@context": "https://schema.org",
      "@type": "DonateAction",
      name: `Donate to ${p.title}`,
      recipient: { "@type": "Organization", name: "Raise Sri Lanka" },
      target: absoluteUrl(`/donate?project_id=${p.id}&type=one_time`),
    });
  }

  blocks.push(
    buildBreadcrumbsJsonLd([
      { name: "Home", url: "/" },
      { name: "Projects", url: "/projects" },
      { name: p.title, url: `/projects/${p.slug}` },
    ]),
  );

  return blocks;
}

export type FundraiserSeo = {
  id: string;
  slug: string;
  title: string;
  story?: string | null;
  hero_image?: string | null;
  goal_cents?: number | null;
  raised_cents?: number | null;
  status?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export function buildFundraiserJsonLd(f: FundraiserSeo, project?: { title: string; slug: string } | null) {
  const url = absoluteUrl(`/fundraisers/${f.slug}`);
  const description = truncate(f.story, 300);
  const blocks: object[] = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: f.title,
      description,
      image: f.hero_image ? [f.hero_image] : undefined,
      datePublished: f.created_at ?? undefined,
      dateModified: f.updated_at ?? f.created_at ?? undefined,
      mainEntityOfPage: url,
      inLanguage: "en",
      publisher: {
        "@type": "Organization",
        name: "Raise Sri Lanka",
        logo: { "@type": "ImageObject", url: `${siteOrigin()}/favicon.png` },
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "DonateAction",
      name: `Donate to ${f.title}`,
      recipient: { "@type": "Organization", name: "Raise Sri Lanka" },
      target: absoluteUrl(`/donate?fundraiser_id=${f.id}&type=one_time`),
    },
  ];

  const crumbs = [
    { name: "Home", url: "/" },
    { name: "Fundraisers", url: "/fundraisers" },
  ];
  if (project) crumbs.push({ name: project.title, url: `/projects/${project.slug}` });
  crumbs.push({ name: f.title, url: `/fundraisers/${f.slug}` });
  blocks.push(buildBreadcrumbsJsonLd(crumbs));

  return blocks;
}

export function buildFaqJsonLd(items: { question: string; answer: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.question,
      acceptedAnswer: { "@type": "Answer", text: it.answer },
    })),
  };
}

export function buildItemListJsonLd(items: { name: string; url: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      url: absoluteUrl(it.url),
    })),
  };
}
